import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Search,
  CheckCircle,
  Schedule,
  Cancel,
  Info,
  Phone,
  Email,
  CalendarToday,
  Payment,
  School,
  Person
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import axios from 'axios';

const TrackApplication = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  
  const [trackingId, setTrackingId] = useState('');
  const [loading, setLoading] = useState(false);
  const [applicationData, setApplicationData] = useState(null);
  const [error, setError] = useState('');

  // Get tracking ID from URL params if available
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlTrackingId = params.get('id');
    if (urlTrackingId) {
      setTrackingId(urlTrackingId);
      handleTrackApplication(urlTrackingId);
    }
  }, [location]);

  const handleTrackApplication = async (id = trackingId) => {
    if (!id.trim()) {
      setError('Please enter a tracking ID');
      return;
    }

    setLoading(true);
    setError('');
    setApplicationData(null);

    try {
      const response = await axios.get(`/api/enhanced-admissions/track/${id.trim()}`);
      
      if (response.data.success) {
        setApplicationData(response.data.data);
        enqueueSnackbar('Application found successfully', { variant: 'success' });
      } else {
        setError(response.data.message || 'Application not found');
      }
    } catch (error) {
      console.error('Track application error:', error);
      const errorMessage = error.response?.data?.message || 'Error tracking application. Please try again.';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Approved':
        return <CheckCircle sx={{ color: 'success.main' }} />;
      case 'Rejected':
        return <Cancel sx={{ color: 'error.main' }} />;
      default:
        return <Schedule sx={{ color: 'warning.main' }} />;
    }
  };

  const getStatusColor = (statusColor) => {
    switch (statusColor) {
      case 'success':
        return 'success';
      case 'error':
        return 'error';
      default:
        return 'warning';
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <School sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom color="primary.main">
            Track Your Application
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Enter your tracking ID to check the status of your admission application
          </Typography>
        </Box>

        {/* Search Form */}
        <Card sx={{ mb: 4, bgcolor: 'grey.50' }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label="Tracking ID"
                  placeholder="Enter your tracking ID (e.g., EMIS123456789)"
                  value={trackingId}
                  onChange={(e) => setTrackingId(e.target.value?.toUpperCase() || '')}
                  variant="outlined"
                  InputProps={{
                    startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={() => handleTrackApplication()}
                  disabled={loading || !trackingId.trim()}
                  startIcon={loading ? <CircularProgress size={20} /> : <Search />}
                >
                  {loading ? 'Searching...' : 'Track Application'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Application Details */}
        {applicationData && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h5" gutterBottom color="primary.main">
                Application Details
              </Typography>
              
              {/* Status Section */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item>
                    {getStatusIcon(applicationData.status)}
                  </Grid>
                  <Grid item xs>
                    <Typography variant="h6" gutterBottom>
                      Application Status
                    </Typography>
                    <Chip 
                      label={applicationData.applicationStatus || applicationData.status} 
                      color={getStatusColor(applicationData.statusColor)}
                      size="large"
                      sx={{ mr: 1 }}
                    />
                    {applicationData.admissionStatus && (
                      <Chip 
                        label={`Admission: ${applicationData.admissionStatus}`}
                        color="info"
                        size="small"
                      />
                    )}
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Admin Message and Important Dates */}
              {(applicationData.adminMessage || applicationData.entranceTest?.status || applicationData.interview?.date) && (
                <Box sx={{ mb: 3 }}>
                  {applicationData.adminMessage && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>Admin Message</Typography>
                      <Typography variant="body2">{applicationData.adminMessage}</Typography>
                    </Alert>
                  )}
                  <Grid container spacing={2}>
                    {applicationData.entranceTest?.status && (
                      <Grid item xs={12} md={6}>
                        <Card variant="outlined">
                          <CardContent>
                            <Typography variant="subtitle1" gutterBottom>Entrance Test</Typography>
                            <Typography variant="body2">Status: {applicationData.entranceTest.status.replaceAll('_', ' ')}</Typography>
                            <Typography variant="body2">
                              Date: {applicationData.entranceTest.date ? new Date(applicationData.entranceTest.date).toLocaleString() : 'TBD'}
                            </Typography>
                            {applicationData.entranceTest.score != null && (
                              <Typography variant="body2">Score: {applicationData.entranceTest.score}</Typography>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    )}
                    {applicationData.interview?.date && (
                      <Grid item xs={12} md={6}>
                        <Card variant="outlined">
                          <CardContent>
                            <Typography variant="subtitle1" gutterBottom>Interview</Typography>
                            <Typography variant="body2">
                              Date: {new Date(applicationData.interview.date).toLocaleString()}
                            </Typography>
                            {applicationData.interview.notes && (
                              <Typography variant="body2">Notes: {applicationData.interview.notes}</Typography>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Application Information */}
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <Info color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Tracking ID"
                        secondary={applicationData.trackingId}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Person color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Student Name"
                        secondary={applicationData.studentInfo?.name || 'N/A'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <School color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Applied Class"
                        secondary={applicationData.studentInfo?.class || 'N/A'}
                      />
                    </ListItem>
                  </List>
                </Grid>
                <Grid item xs={12} md={6}>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <CalendarToday color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Submission Date"
                        secondary={new Date(applicationData.submissionDate).toLocaleDateString()}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Payment color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Application Fee"
                        secondary={`₹${applicationData.paymentInfo?.fee || 'N/A'}`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Payment Status"
                        secondary={applicationData.paymentInfo?.status?.toUpperCase() || 'UNKNOWN'}
                      />
                    </ListItem>
                  </List>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Contact Information */}
              <Typography variant="h6" gutterBottom color="primary.main">
                Contact Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Phone sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      Phone: {applicationData.studentInfo?.phone || 'N/A'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Email sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      Email: {applicationData.studentInfo?.email || 'N/A'}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* Status-specific information */}
              {applicationData.status === 'Under Review' && (
                <Alert severity="info" sx={{ mt: 3 }}>
                  <Typography variant="body2">
                    <strong>Your application is currently under review.</strong><br/>
                    • Review process typically takes 3-5 business days<br/>
                    • You will be notified via email once a decision is made<br/>
                    • For urgent queries, contact admissions@emis.edu
                  </Typography>
                </Alert>
              )}

              {applicationData.status === 'Approved' && (
                <Alert severity="success" sx={{ mt: 3 }}>
                  <Typography variant="body2">
                    <strong>Congratulations! Your application has been approved.</strong><br/>
                    • You will receive admission confirmation via email<br/>
                    • Please complete the enrollment process as instructed<br/>
                    • Contact admissions@emis.edu for next steps
                  </Typography>
                </Alert>
              )}

              {applicationData.status === 'Rejected' && (
                <Alert severity="error" sx={{ mt: 3 }}>
                  <Typography variant="body2">
                    <strong>We regret to inform you that your application was not successful.</strong><br/>
                    • You will receive detailed feedback via email<br/>
                    • You may reapply in the next admission cycle<br/>
                    • Contact admissions@emis.edu for more information
                  </Typography>
                </Alert>
              )}
            </CardContent>
          </Card>
        )}

        {/* Instructions */}
        <Card sx={{ bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary.main">
              Instructions
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Your tracking ID was provided when you submitted your application<br/>
              • If you can't find your tracking ID, check your email confirmation<br/>
              • For technical issues or queries, contact our support team<br/>
              • Keep checking this page for status updates
            </Typography>
          </CardContent>
        </Card>

        {/* Navigation */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/')}
            sx={{ mr: 2 }}
          >
            Back to Home
          </Button>
          <Button
            variant="contained"
            onClick={() => navigate('/admission')}
          >
            New Application
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default TrackApplication;
